import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { v4 as uuidv4 } from "uuid";

// API
import api from "../../store/services/api";

// MUI Components
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

// Animated Modal Components
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../modals/AnimatedModal";

// Icons
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BackpackIcon from "@mui/icons-material/Backpack";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

// Constants & Utils
import { PHONE_HELPER, STRING_NOT_NULL_HELPER } from "../../common/helpersTxt";
import { validatePhone, validateString } from "../../common/validators";
import DeliveryServiceFilter from "../search/deliveryServiceFilter";
import { StyledTextField } from "../styled/inputs/textInput";
import { createDeliveryService } from "../../api/DeliveryService";

import { phoneMask } from "../../utils/formatters";
import { extractApiErrorMessage } from "../../utils/subscriptionErrors";

// Animation variants
const formItemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: (custom) => ({
    y: 0,
    opacity: 1,
    transition: {
      delay: custom * 0.1,
      duration: 0.4,
      ease: "easeOut",
    },
  }),
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

const bagOptions = [
  {
    value: "small",
    label: "Pequena",
    description: "Ideal para pequenas entregas",
    dimensions: "45cm x 50cm x 20cm",
    weight: "até 75kg",
  },
  {
    value: "medium",
    label: "Média",
    description: "Boa capacidade para entregas padrão",
    dimensions: "45cm x 50cm x 20cm",
    weight: "até 75kg",
  },
  {
    value: "big",
    label: "Grande",
    description: "Máxima capacidade para grandes entregas",
    dimensions: "45cm x 50cm x 20cm",
    weight: "até 75kg",
  },
];

const DriversCreateEdit = ({ id, closeModal, updateDrivers, show }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [deliveryService, setDeliveryService] = useState(null);
  const [showNewDeliveryServiceForm, setShowNewDeliveryServiceForm] =
    useState(false);
  const [newDeliveryServiceName, setNewDeliveryServiceName] = useState("");
  const [newDeliveryServicePhone, setNewDeliveryServicePhone] = useState("");
  const [isDeliveryServiceNameValid, setIsDeliveryServiceNameValid] =
    useState(true);

  const [edit, setEdit] = useState(false);
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [phone, setPhone] = useState("");
  const [plate, setPlate] = useState("");
  const [model, setModel] = useState("");
  const [brand, setBrand] = useState("");
  const [color, setColor] = useState("");
  const [width, setWidth] = useState(0);
  const [length, setLength] = useState(0);
  const [height, setHeight] = useState(0);
  const [cubage, setCubage] = useState(0);
  const [loadableWeight, setLoadableWeight] = useState(0);
  const [title, setTitle] = useState("Novo Entregador");
  const [selectedBag, setSelectedBag] = useState("small");
  const [vehicleId, setVehicleId] = useState(uuidv4());
  const [driverId, setDriverId] = useState(uuidv4());

  const [isNameValid, setIsNameValid] = useState(true);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    switch (selectedBag) {
      case "small":
        setWidth(50);
        setLength(45);
        setHeight(20);
        setLoadableWeight(75);
        break;

      case "medium":
        setWidth(50);
        setLength(45);
        setHeight(20);
        setLoadableWeight(75);
        break;

      case "big":
        setWidth(50);
        setLength(45);
        setHeight(20);
        setLoadableWeight(75);
        break;

      default:
        break;
    }
  }, [selectedBag]);

  const loadData = async () => {
    try {
      if (id) {
        setLoading(true);
        const driver = await api.get(`/drivers?did=${id}`);
        const vehicle = await api.get(
          `/vehicles?vid=${driver.data.fk_vehicle}`
        );

        if (driver.data.fk_delivery_service) {
          const deliveryServiceData = await api.get(
            `/delivery-services/${driver.data.fk_delivery_service}`
          );
          setDeliveryService({
            label: deliveryServiceData.data.name,
            value: deliveryServiceData.data.id,
          });
        }

        var selected_bag;
        switch (vehicle.data.width) {
          case 50:
            selected_bag = "small";
            break;
          case 39:
            selected_bag = "medium";
            break;
          case 45:
            selected_bag = "big";
            break;
          default:
            selected_bag = "small";
            break;
        }

        setEdit(true);
        setDriverId(driver.data.driver_id);
        setName(driver.data.name);
        setCpf(driver.data.cpf);
        setPhone(driver.data.phone);
        setPlate(vehicle.data.plate);
        setModel(vehicle.data.model);
        setBrand(vehicle.data.brand);
        setColor(vehicle.data.color);
        setWidth(vehicle.data.width);
        setLength(vehicle.data.length);
        setHeight(vehicle.data.height);
        setCubage(vehicle.data.cubage);
        setLoadableWeight(vehicle.data.loadable_weight);
        setVehicleId(driver.data.fk_vehicle);
        setTitle("Editar Entregador");
        setSelectedBag(selected_bag);
      }
    } catch (err) {
      console.log(err);
      setError("Erro ao carregar dados do entregador");
    } finally {
      setLoading(false);
    }
  };

  // Handler for delivery service selection
  const handleDeliveryServiceChange = (selected) => {
    if (selected && selected.value === "create_new") {
      setShowNewDeliveryServiceForm(true);
      setDeliveryService(selected);
    } else {
      setShowNewDeliveryServiceForm(false);
      setDeliveryService(selected);
    }
  };

  // Function to create a new delivery service
  const createNewDeliveryService = async () => {
    try {
      // Validate delivery service name
      if (!newDeliveryServiceName.trim()) {
        setIsDeliveryServiceNameValid(false);
        return null;
      }

      const response = await createDeliveryService({
        name: newDeliveryServiceName,
        phone: newDeliveryServicePhone.replace(/\D/g, ""), // cleaned phone number
      });

      const newService = {
        label: response.data.name,
        value: response.data.id,
      };

      setDeliveryService(newService);
      setShowNewDeliveryServiceForm(false);

      return newService;
    } catch (err) {
      console.log(err);
      setError("Erro ao criar novo serviço de entrega");
      return null;
    }
  };

  const handleSelectedBagChange = (event) => {
    setSelectedBag(event.target.value);
  };

  const createDriver = async () => {
    try {
      // If "Criar Novo" is selected and form is shown, create the delivery service first
      let deliveryServiceId = deliveryService?.value;

      if (
        deliveryService?.value === "create_new" &&
        showNewDeliveryServiceForm
      ) {
        const newService = await createNewDeliveryService();
        if (newService) {
          deliveryServiceId = newService.value;
        } else {
          setSubmitting(false);
          return;
        }
      }

      // Remove non-digit characters from phone before saving
      const cleanedPhone = phone.replace(/\D/g, "");

      const driver = {
        driver_id: driverId,
        name: name,
        cpf: cpf,
        status: "online",
        fk_vehicle: vehicleId,
        phone: cleanedPhone,
        fk_delivery_service:
          deliveryServiceId !== "create_new" ? deliveryServiceId : null,
      };

      if (edit)
        await api.put(`/drivers/?did=${driverId}`, driver, {
          headers: { "Content-Type": "application/json" },
        });
      else
        await api.post("/drivers/", driver, {
          headers: { "Content-Type": "application/json" },
        });

      closeModal();
      updateDrivers();
    } catch (err) {
      console.log(JSON.stringify(err));
      setError(
        extractApiErrorMessage(err, "Erro ao salvar dados do entregador")
      );
      setSubmitting(false);
    }
  };

  const createVehicle = async () => {
    try {
      const validate = phone.length >= 11 && name.length >= 3;

      if (!validate) {
        setError("Informações faltando ou incorretas");
        setSubmitting(false);
        return;
      }

      const newCubage = width * length * height;
      setCubage(newCubage);

      const vehicle = {
        vehicle_id: vehicleId,
        cubage: newCubage,
        width: width,
        length: length,
        height: height,
        loadable_weight: loadableWeight,
        plate: plate,
        brand: brand,
        model: model,
        color: color,
      };

      if (edit) {
        const res = await api.put(`/vehicles/?vid=${vehicleId}`, vehicle, {
          headers: { "Content-Type": "application/json" },
        });
        if (res) createDriver();
        return;
      }

      await api.post("/vehicles/", vehicle, {
        headers: { "Content-Type": "application/json" },
      });
      createDriver();
    } catch (err) {
      console.log(err);
      setError("Erro ao salvar dados do veículo");
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setError("");
    setIsNameValid(validateString(name));
    setIsPhoneValid(validatePhone(phone));

    // Validate delivery service name if creating new
    if (deliveryService?.value === "create_new") {
      setIsDeliveryServiceNameValid(validateString(newDeliveryServiceName));
    }

    setSubmitted(true);

    const isValid =
      validateString(name) &&
      validatePhone(phone) &&
      (deliveryService?.value !== "create_new" ||
        validateString(newDeliveryServiceName));

    if (isValid) {
      setSubmitting(true);
      try {
        await createVehicle();
      } catch (err) {
        setError("Ocorreu um erro ao salvar os dados");
        setSubmitting(false);
      }
    }
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="80%"
      maxWidth="800px"
    >
      <ModalHeader title={title} icon={PersonIcon} onClose={closeModal} />

      <ModalContent>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress size={50} thickness={4} />
            <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
              Carregando informações...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ px: 1, py: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Driver Information Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.7),
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <PersonIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Informações do Entregador
                    </Typography>
                  </Stack>

                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <motion.div
                        custom={0}
                        variants={formItemVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <StyledTextField
                          label="Nome"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          variant="outlined"
                          fullWidth
                          size="small"
                          error={!isNameValid && submitted}
                          helperText={
                            !isNameValid && submitted
                              ? STRING_NOT_NULL_HELPER
                              : ""
                          }
                        />
                      </motion.div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <motion.div
                        custom={1}
                        variants={formItemVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <StyledTextField
                          label="Telefone"
                          required
                          value={phone}
                          onChange={(e) => setPhone(phoneMask(e.target.value))}
                          variant="outlined"
                          fullWidth
                          size="small"
                          error={!isPhoneValid && submitted}
                          helperText={
                            !isPhoneValid && submitted ? PHONE_HELPER : ""
                          }
                          inputProps={{ maxLength: 15 }}
                        />
                      </motion.div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <motion.div
                        custom={2}
                        variants={formItemVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <StyledTextField
                          label="CPF"
                          value={cpf}
                          onChange={(e) => setCpf(e.target.value)}
                          variant="outlined"
                          fullWidth
                          size="small"
                          inputProps={{ maxLength: 11 }}
                        />
                      </motion.div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <motion.div
                        custom={2}
                        variants={formItemVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <DeliveryServiceFilter
                          selectedValue={deliveryService}
                          onFilterChange={handleDeliveryServiceChange}
                          allowCreation={true}
                        />

                        {/* Show form for creating a new delivery service if "Criar Novo" is selected */}
                        {showNewDeliveryServiceForm && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              borderRadius: 2,
                              backgroundColor: alpha(
                                theme.palette.background.paper,
                                0.7
                              ),
                              border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.2
                              )}`,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              color="primary"
                              gutterBottom
                            >
                              Novo Serviço de Entrega
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <StyledTextField
                                  label="Nome do Serviço"
                                  required
                                  value={newDeliveryServiceName}
                                  onChange={(e) =>
                                    setNewDeliveryServiceName(e.target.value)
                                  }
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  sx={{ mb: 2 }}
                                  error={
                                    !isDeliveryServiceNameValid && submitted
                                  }
                                  helperText={
                                    !isDeliveryServiceNameValid && submitted
                                      ? STRING_NOT_NULL_HELPER
                                      : ""
                                  }
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <StyledTextField
                                  label="Telefone"
                                  value={newDeliveryServicePhone}
                                  onChange={(e) =>
                                    setNewDeliveryServicePhone(
                                      phoneMask(e.target.value)
                                    )
                                  }
                                  variant="outlined"
                                  fullWidth
                                  size="small"
                                  inputProps={{ maxLength: 15 }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </motion.div>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Vehicle Information Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.7),
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <DirectionsCarIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Veículo do Entregador
                    </Typography>
                  </Stack>

                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <motion.div
                        custom={3}
                        variants={formItemVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <StyledTextField
                          label="Placa"
                          required
                          value={plate}
                          onChange={(e) => setPlate(e.target.value)}
                          variant="outlined"
                          fullWidth
                          size="small"
                        />
                      </motion.div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <motion.div
                        custom={4}
                        variants={formItemVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <StyledTextField
                          label="Modelo"
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          variant="outlined"
                          fullWidth
                          size="small"
                        />
                      </motion.div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <motion.div
                        custom={5}
                        variants={formItemVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <StyledTextField
                          label="Marca"
                          value={brand}
                          onChange={(e) => setBrand(e.target.value)}
                          variant="outlined"
                          fullWidth
                          size="small"
                        />
                      </motion.div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <motion.div
                        custom={6}
                        variants={formItemVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <StyledTextField
                          label="Cor"
                          value={color}
                          onChange={(e) => setColor(e.target.value)}
                          variant="outlined"
                          fullWidth
                          size="small"
                        />
                      </motion.div>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Delivery Bag Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.7),
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <BackpackIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Capacidade da Mochila
                    </Typography>
                  </Stack>

                  <Divider sx={{ mb: 3 }} />

                  <motion.div
                    custom={7}
                    variants={formItemVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <FormControl component="fieldset" sx={{ width: "100%" }}>
                      <RadioGroup
                        aria-labelledby="delivery-bag-selection"
                        name="delivery-bag"
                        value={selectedBag}
                        onChange={handleSelectedBagChange}
                      >
                        <Grid container spacing={2}>
                          {bagOptions.map((option) => (
                            <Grid item xs={12} md={4} key={option.value}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  borderColor:
                                    selectedBag === option.value
                                      ? theme.palette.primary.main
                                      : alpha(theme.palette.grey[300], 0.8),
                                  backgroundColor:
                                    selectedBag === option.value
                                      ? alpha(theme.palette.primary.light, 0.1)
                                      : theme.palette.background.paper,
                                  transition: "all 0.2s ease",
                                  cursor: "pointer",
                                  "&:hover": {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(
                                      theme.palette.primary.light,
                                      0.05
                                    ),
                                  },
                                }}
                                onClick={() => setSelectedBag(option.value)}
                              >
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio />}
                                  label={
                                    <Box>
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight={500}
                                      >
                                        {option.label}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {option.description}
                                      </Typography>
                                      <Stack direction="row" spacing={1} mt={1}>
                                        <Chip
                                          label={option.dimensions}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 22 }}
                                        />
                                        <Chip
                                          label={option.weight}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 22 }}
                                        />
                                      </Stack>
                                    </Box>
                                  }
                                  sx={{
                                    m: 0,
                                    width: "100%",
                                    "& .MuiFormControlLabel-label": {
                                      width: "100%",
                                    },
                                  }}
                                />
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </RadioGroup>
                    </FormControl>
                  </motion.div>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </ModalContent>

      <ModalFooter>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            color="inherit"
            onClick={closeModal}
            startIcon={<CloseIcon />}
          >
            Cancelar
          </Button>

          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={buttonVariants}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting || loading}
              startIcon={
                submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
            >
              {submitting ? "Salvando..." : "Salvar Entregador"}
            </Button>
          </motion.div>
        </Stack>
      </ModalFooter>
    </AnimatedModal>
  );
};

export default DriversCreateEdit;
